import React, {useState} from 'react'
import useQuery from '@/hooks/useQuery'
import { useForm } from 'react-hook-form'

import { PrimaryButton, DefaultButton } from '@/shared/Buttons'
import { showNonSpaToast } from '@/utils/nonSpaToast'

// Testimonials are not stored in Closing Credits any more. They live on
// Shosay, and this section just points the landing page at one Shosay widget.
// The old per-testimonial editor is gone with them: there is nothing to edit
// here beyond the section header and which widget to show.
const SHOSAY_WIDGETS_URL = 'https://shosay.com'

const TestimonialHeaderForm = ({course}) => {
  const {landing_page} = course
  const [landingPage, setLandingPage] = useState(landing_page)
  const [widgetUuid, setWidgetUuid] = useState(course.shosay_widget_uuid || '')
  const [editing, setEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const { putpostRequest } = useQuery()
  const { formState, register, handleSubmit, setError } = useForm({
    defaultValues: {
      testimonial_header: landing_page.testimonial_header,
      shosay_widget_uuid: course.shosay_widget_uuid || '',
    }
  })
  const { errors } = formState

  const onSubmit = (data) => {
    const formData = {
      landing_page: { testimonial_header: data.testimonial_header },
      course: { shosay_widget_uuid: data.shosay_widget_uuid.trim() },
    }
    setLoading(true)
    putpostRequest(`/api/v1/teachers/courses/${course.id}/landing_page`, 'POST', formData, (err, jsonData) => {
      setLoading(false)
      if (err) {
        if (err.shosay_widget_uuid) {
          setError('shosay_widget_uuid', { type: 'custom', message: err.shosay_widget_uuid[0] })
        }
        showNonSpaToast("Error", false)
        return
      }

      showNonSpaToast("Saved", true)
      setLandingPage({ ...landingPage, testimonial_header: jsonData.landing_page.testimonial_header })
      setWidgetUuid(jsonData.course.shosay_widget_uuid || '')
      setEditing(false)
    })
  }

  return <>
    { !editing && <div id="testimonials" className="hover:shadow-xl py-2 cursor-pointer hover:opacity-50" onClick={() => setEditing(true)}>
      <div className="text-center mb-4">
        <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-4xl sm:tracking-tight">{landingPage.testimonial_header}</h1>
        <div className="flex text-clcnavy justify-end">edit</div>
      </div>
      <div className="mx-auto flex justify-center">
        { widgetUuid
          ? <div className="flex items-center rounded-full border py-2 px-5 bg-opacity-5 bg-green-100 border-green-200">
              <h6 className="b1 text-green-800">Showing Shosay widget {widgetUuid}</h6>
            </div>
          : <div className="flex items-center rounded-full border py-2 px-5 bg-opacity-5 bg-red-100 border-red-200">
              <h6 className="b1 text-red-800">No widget connected, this section is hidden</h6>
            </div> }
      </div>
    </div> }

    { editing && <form className='p-8 border-4 border-clcpurple rounded-lg'>
      <div className="p-8 pt-5">
        <div className="space-y-6">
          <div>
            <div className="prose-product-lg">
              <h5 className="flex justify-between text-gray-800"><label className="mb-1">Testimonial Section Header</label></h5>
            </div>
            <div className="prose-product-sm flex items-center sm:prose-product-lg">
              <input type="text" className={errors.testimonial_header ? 'errors placeholder-shown:italic placeholder-gray-600 p2 py-3.5 px-4 w-full border rounded-lg' : 'placeholder-shown:italic placeholder-gray-600 p2 py-3.5 px-4 w-full border rounded-lg'} {...register('testimonial_header', { required: true }) } placeholder="What People Are Saying" />
            </div>
            { errors.testimonial_header && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
          </div>

          <div>
            <div className="prose-product-lg">
              <h5 className="flex justify-between text-gray-800"><label className="mb-1">Shosay Widget ID</label></h5>
            </div>
            <p className="p2 text-gray-600 mb-2">
              Testimonials are managed on <a href={SHOSAY_WIDGETS_URL} target="_blank" rel="noopener noreferrer" className="text-clcnavy underline">shosay.com</a>.
              Build a widget there, then paste its ID here. Leave this blank and the testimonial section will not appear on your landing page at all.
            </p>
            <div className="prose-product-sm flex items-center sm:prose-product-lg">
              <input type="text" className={errors.shosay_widget_uuid ? 'errors placeholder-shown:italic placeholder-gray-600 p2 py-3.5 px-4 w-full border rounded-lg' : 'placeholder-shown:italic placeholder-gray-600 p2 py-3.5 px-4 w-full border rounded-lg'} {...register('shosay_widget_uuid') } placeholder="8438f393-491e-413b-bde4-e2ef7a039a0c" />
            </div>
            { errors.shosay_widget_uuid && <div className='mt-2 text-sm text-red-600'>{errors.shosay_widget_uuid.message || 'That does not look like a Shosay widget ID.'}</div> }
          </div>

          <div className='flex gap-x-5'>
            <PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} text="Save" />
            <DefaultButton onClick={() => setEditing(false)} text="Cancel" />
          </div>
        </div>
      </div>
    </form> }
  </>
}

export default TestimonialHeaderForm
